import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Clock, ArrowRightLeft, Loader2, Package, Scale, Ruler, FileText,
  Calculator, Plus, Trash2, CheckCircle2, User, Phone, Mail,
  MapPin, ShieldCheck, CreditCard, Check, Download, Lock as LockIcon,
  Truck, Globe, ArrowRight, ArrowLeft, MessageCircle, Sparkles,
  Building, ChevronRight, X, ExternalLink
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';
import CheckoutPayment from '../../components/CheckoutPayment'; // Ajusta la ruta si es necesario
import ReCAPTCHA from "react-google-recaptcha";
import 'react-phone-number-input/style.css'
import PhoneInput from 'react-phone-number-input'
import { isValidPhoneNumber } from 'react-phone-number-input';
import { Turnstile } from '@marsidev/react-turnstile';



{/* ========================================================
    1. COMPONENTE: ENCABEZADO (HERO)
    ======================================================== */}
function HeaderCotizador() {
  return (
    <div className="w-full relative overflow-hidden mb-8">
      <div className="absolute inset-0 bg-gradient-to-b from-[#CE010B]/80 via-black/70 to-transparent dark:from-[#590000]/90 dark:via-black/80 dark:to-transparent pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pt-10 pb-12 flex flex-col items-center text-center">
        <div className="flex flex-col md:flex-row items-center justify-center gap-5 mb-6">
          <h1 className="text-[38px] md:text-[48px] font-bold text-white uppercase tracking-tighter leading-tight drop-shadow-md" style={{ fontFamily: "'Secular One', sans-serif" }}>
            Cotiza tu envío <br className="hidden md:block" /> en segundos
          </h1>
          <div className="bg-white/10 p-3 rounded-full backdrop-blur-md border-[2px] border-white/50 shadow-[0_0_30px_rgba(255,255,255,0.2)] animate-pulse">
            <Clock size={36} strokeWidth={2} className="text-white" />
          </div>
        </div>
        <p className="text-white/95 text-[15px] md:text-[18px] font-medium max-w-[800px] leading-relaxed mb-6 drop-shadow-sm">
          Obtén una tarifa transparente y competitiva al instante. Planea tus envíos con la tranquilidad de que llegarán a tiempo y en perfectas condiciones.
        </p>
        <div className="w-full max-w-[850px] h-[2px] bg-gradient-to-r from-transparent via-white/50 to-transparent"></div>
      </div>
    </div>
  );
}

{/* ========================================================
    2. COMPONENTE: FLUJO COMPLETO DE COTIZACIÓN Y CHECKOUT
    ======================================================== */}
function FormularioCotizacion() {
  const turnstileRef = useRef(null);
  const [phone, setPhone] = useState();
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isGuideReady, setIsGuideReady] = useState(false); // 👈 NUEVO
  const [isCheckingEstatus, setIsCheckingEstatus] = useState(false); // 👈 NUEVO
  const [paymentResult, setPaymentResult] = useState(null); // Ya deberías tenerlo
  const { user, authenticated } = useAuth();
  const [sucursales, setSucursales] = useState([]);
  const [destinosDisponibles, setDestinosDisponibles] = useState([]);
  const [origen, setOrigen] = useState('');
  const [destino, setDestino] = useState('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [resultadoFinal, setResultadoFinal] = useState(null); // Para guardar el # de guía y datos finales
  const [paquetes, setPaquetes] = useState([
    { id: Date.now(), peso: '', dimensiones: { largo: '', ancho: '', alto: '' }, descripcion: '' }
  ]);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [errorGlobal, setErrorGlobal] = useState(null);
  const [tipoEnvio, setTipoEnvio] = useState('express');
  const [pasoCheckout, setPasoCheckout] = useState(0);
  const [datosRemitente, setDatosRemitente] = useState({ nombre: '', telefono: '', correo: '' });
  const [datosDestinatario, setDatosDestinatario] = useState({ nombre: '', telefono: '' });
  const [confirmaContenido, setConfirmaContenido] = useState(false);
  useEffect(() => {
    let intervalId;
    let intentos = 0; // 🛡️ Evitar ejecuciones infinitas
    const MAX_INTENTOS = 60; // 3 minutos aprox

    if (paymentResult?.status === 'approved' && !isGuideReady) {
      setIsCheckingEstatus(true);

      const verificarEstatusReal = async () => {
        intentos++;
        try {
          const response = await api.get(`/shipments/${resultadoFinal?.tracking_number}/status`);

          if (response.data.is_paid) {
            setIsGuideReady(true);
            setIsCheckingEstatus(false);
            clearInterval(intervalId);
          }
        } catch (error) {
          console.log("Sincronizando...");
        }

        if (intentos >= MAX_INTENTOS) {
          clearInterval(intervalId);
          setIsCheckingEstatus(false);
          setErrorGlobal("El sistema tardó en confirmar el pago. Por favor, recarga o contacta a soporte.");
        }
      };

      intervalId = setInterval(verificarEstatusReal, 3000);
      verificarEstatusReal();
    }

    return () => clearInterval(intervalId);
  }, [paymentResult, isGuideReady, resultadoFinal?.tracking_number]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoadingBranches(true);
        const response = await api.get('/branches');
        const raw = response.data?.data || response.data;
        setSucursales(Array.isArray(raw) ? raw : []);
      } catch (err) {
        setErrorGlobal("Error al consultar las sucursales disponibles.");
        setSucursales([]);
      } finally {
        setLoadingBranches(false);
      }
    };
    fetchBranches();
  }, []);
  useEffect(() => {
    // Si 'user' existe, ya tenemos la data necesaria
    if (user && (user.name || user.email)) {
      console.log("✅ Llenando datos del remitente con:", user.name);
      const rawPhone = user.phone || user.telefono || user.celular || '';
      let formattedPhone = rawPhone;
      if (rawPhone && !rawPhone.startsWith('+')) {
        formattedPhone = rawPhone.length === 10 ? `+52${rawPhone}` : `+${rawPhone}`;
      }
      setDatosRemitente({
        nombre: user.name || '',
        telefono: formattedPhone || '',
        correo: user.email || ''
      });
    }
  }, [user]); // Solo dependemos de que 'user' cambie o cargue
  const handleGuardar = () => {
    // Valida que el número tenga sentido para el país seleccionado
    if (!datosDestinatario.telefono || !isValidPhoneNumber(datosDestinatario.telefono)) {
      alert("El número de teléfono no parece válido. Por favor, verifica el código de país y el número.");
      return;
    }

    // Si pasa la validación, continúas con tu lógica
    enviarDatosAlBackend();
  };
  const handleOrigenChange = async (e) => {
    const selectedId = e.target.value;
    setOrigen(selectedId);
    setDestino('');
    setDestinosDisponibles([]);
    setResultado(null);
    setPasoCheckout(0);

    if (selectedId) {
      try {
        setLoadingRoutes(true);
        const response = await api.get(`/branches/${selectedId}/routes`);
        const raw = response.data?.data || response.data;
        setDestinosDisponibles(Array.isArray(raw) ? raw : []);
      } catch (err) {
        setErrorGlobal("Error al consultar las rutas disponibles.");
        setDestinosDisponibles([]);
      } finally {
        setLoadingRoutes(false);
      }
    }
  };

  const handleSwap = () => {
    if (!origen || !destino) return;
    setOrigen(destino);
    setDestino(origen);
    setResultado(null);
    setPasoCheckout(0);
  };

  const agregarPaquete = () => setPaquetes([...paquetes, { id: Date.now(), peso: '', dimensiones: { largo: '', ancho: '', alto: '' }, descripcion: '' }]);
  const eliminarPaquete = (id) => { if (paquetes.length > 1) setPaquetes(paquetes.filter(p => p.id !== id)); };
  const actualizarPaquete = (id, campo, valor) => {
    setPaquetes(paquetes.map(p => p.id === id ? (['largo', 'ancho', 'alto'].includes(campo) ? { ...p, dimensiones: { ...p.dimensiones, [campo]: valor } } : { ...p, [campo]: valor }) : p));
  };

  const handleCotizar = async (e) => {
    e.preventDefault();
    setErrorGlobal(null);
    setResultado(null);
    setPasoCheckout(0);

    // 1. Validación de seguridad (Captcha)
    // Permitimos omitir esta validación en el frontend en el entorno de desarrollo (DEV) para facilitar pruebas desde cualquier dispositivo o IP local.
    if (!captchaToken && !import.meta.env.DEV) {
        setErrorGlobal("Por favor, verifica que eres humano completando el captcha.");
        return;
    }

    // 2. Validación de datos antes de enviar al servidor
    // Esto evita peticiones innecesarias si el usuario no llenó bien los campos
    const hayPesoInvalido = paquetes.some(p => !p.peso || parseFloat(p.peso) <= 0);
    if (hayPesoInvalido) {
      setErrorGlobal("Por favor, ingresa un peso válido mayor a 0 para todos los paquetes.");
      return;
    }

    // 3. Preparación del payload
    const payloadPaquetes = paquetes.map(p => ({
      peso: parseFloat(p.peso),
      // Si también envías dimensiones, podrías incluirlas aquí para mayor precisión
      largo: parseFloat(p.dimensiones.largo || 0),
      ancho: parseFloat(p.dimensiones.ancho || 0),
      alto: parseFloat(p.dimensiones.alto || 0)
    }));

    try {
      setLoadingQuote(true);

      const response = await api.post('/cotizar', {
        origen_id: parseInt(origen),
        destino_id: parseInt(destino),
        paquetes: payloadPaquetes,
        recaptcha_token: captchaToken
      });

      // Si todo sale bien
      setResultado(response.data);
    } catch (err) {
      // Manejo de errores profesional
      const mensajeError = err.response?.data?.mensaje || "Error al calcular la tarifa. Intenta nuevamente.";
      setErrorGlobal(mensajeError);
    } finally {
      setLoadingQuote(false);
    }
  };

  // 2. FUNCIÓN DE CAPTCHA (Esta es la única que necesitas)
  const onCaptchaChange = (token) => {
    setCaptchaToken(token);
  };

  let precioNormal = 0;
  let precioExpress = 0;

  // NUEVA LÓGICA: Leemos el Gran Total directamente desde el arreglo "opciones"
  if (resultado && resultado.opciones) {
    const opcionEstandar = resultado.opciones.find(opt => opt.service_type === 'estandar');
    if (opcionEstandar) precioNormal = Number(opcionEstandar.price);

    const opcionExpress = resultado.opciones.find(opt => opt.service_type === 'expres');
    if (opcionExpress) precioExpress = Number(opcionExpress.price);
  }

  const ahora = new Date();
  const esAntesDeCorte = ahora.getHours() < 13;

  const fechaExpress = new Date(ahora);
  if (!esAntesDeCorte) fechaExpress.setDate(fechaExpress.getDate() + 1);

  const fechaNormal = new Date(fechaExpress);
  fechaNormal.setDate(fechaNormal.getDate() + 1);

  const iniciarCheckout = () => {
    setPasoCheckout(1);
    window.scrollTo({ top: 400, behavior: 'smooth' });
  };

  const procesarDatosGuia = async (e) => {
    e.preventDefault();
    setErrorGlobal(null);

    if (!confirmaContenido) {
      setErrorGlobal("Debe confirmar que el contenido es lícito.");
      return;
    }

    try {
      setLoadingQuote(true); // Reutilizamos el estado de carga para el botón

      // Armamos el "paquete de datos" exacto para Laravel
      const payload = {
        sender_name: datosRemitente.nombre,
        sender_email: datosRemitente.correo,
        sender_phone: datosRemitente.telefono,
        receiver_name: datosDestinatario.nombre,
        receiver_phone: datosDestinatario.telefono,
        origin_id: parseInt(origen),
        destination_id: parseInt(destino),
        service_type: tipoEnvio === 'normal' ? 'estandar' : 'expres',
        packages: paquetes.map(p => ({
          weight: parseFloat(p.peso),
          description: p.descripcion || "Sin descripción",
          length: parseFloat(p.dimensiones.largo || 0),
          width: parseFloat(p.dimensiones.ancho || 0),
          height: parseFloat(p.dimensiones.alto || 0)
        }))
      };

      // Enviamos a tu ShipmentController
      const response = await api.post('/shipments', payload);

      // Guardamos la respuesta (trae el tracking_number y el ID)
      setResultadoFinal(response.data.data);
      setPasoCheckout(2); // Avanzamos a la pantalla de éxito/pago

    } catch (err) {
      console.error("Error al guardar envío:", err);
      setErrorGlobal(err.response?.data?.error || "No se pudo registrar el envío en la base de datos.");
    } finally {
      setLoadingQuote(false);
    }
  };
  const handleDescargarGuia = () => {
    // 1. Verificamos que tengamos el tracking_number en el estado
    if (!resultadoFinal?.tracking_number) {
      setErrorGlobal("No se encontró el número de rastreo para la descarga.");
      return;
    }

    const tracking = resultadoFinal.tracking_number;
    const baseUrl = import.meta.env.VITE_BACKEND_URL || 'https://tbt-backend-logistica-production.up.railway.app';

    // Tu ruta de api.php con el prefijo /api/
    const url = `${baseUrl}/api/shipments/${tracking}/label`;

    // 2. Disparamos la descarga en una NUEVA PESTAÑA
    // Al usar '_blank', el navegador abre la pestaña, descarga el PDF y 
    // tu página de cotización se mantiene intacta.
    window.open(url, '_blank');
  };
  const finalizarEntrega = async (shipmentId) => {
    try {
      // Solo enviamos la señal de 'entregado'
      const response = await api.put(`/shipments/${shipmentId}/status`, { status: 'entregado' });

      // Si la API responde OK, actualizamos el resultadoFinal o la lista de unidades
      if (response.status === 200) {
        alert("Paquete entregado y unidad NP300 liberada con éxito.");
        // Aquí podrías disparar una función para recargar los datos de la flota
      }
    } catch (error) {
      console.error("Error al procesar la entrega:", error);
      alert("No se pudo completar la operación.");
    }
  };
  // 💡 Diccionario para traducir los IDs técnicos a nombres comerciales
  const storeNames = {
    'oxxo': 'OXXO',
    '7_eleven': '7-Eleven',
    'soriana': 'Soriana',
    'farmacias_yza': 'Farmacias YZA',
    'santander': 'Santander',
    'kiosko': 'Kiosko',
    'extra': 'Tiendas Extra',
    'paycash': 'el establecimiento',
    'ticket': 'el punto de pago seleccionado'
  };

  // Obtenemos el nombre de la tienda si hay un pago pendiente
  let labelTienda = 'OXXO'; // Por defecto
  if (paymentResult && paymentResult.status !== 'approved') {
    // CheckoutPayment nos debería mandar tienda_exacta, si no, intentamos con method_id
    const idTienda = paymentResult.tienda_exacta || paymentResult.payment_method_id || 'oxxo';
    labelTienda = storeNames[idTienda] || idTienda.replace('_', ' ').toUpperCase();
  }

  return (
    <div className="w-full max-w-[92%] md:max-w-[680px] lg:max-w-5xl mx-auto flex flex-col gap-6 relative z-20 mt-4">

      {errorGlobal && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-2xl text-center font-bold animate-pulse shadow-sm">
          {errorGlobal}
        </div>
      )}

      {/* TARJETA 1: RUTAS */}
      {pasoCheckout < 2 && (
        <div className="w-full bg-gradient-to-b from-[#F1F1F1] to-[#C1C1C1] dark:from-[#590000] dark:to-[#780000] border-[1px] border-black/20 dark:border-white/30 rounded-3xl p-8 shadow-md transition-all">
          <h3 className="text-center text-3xl lg:text-2xl font-bold text-black dark:text-white mb-8 tracking-tight" style={{ fontFamily: "'Secular One', sans-serif" }}>
            Detalles del envío
          </h3>
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4 lg:gap-8">

            {/* Select Origen */}
            <select
              value={origen}
              onChange={handleOrigenChange}
              disabled={loadingBranches || pasoCheckout > 0}
              className="w-full bg-white/50 dark:bg-white/10 dark:text-white border-[1.5px] border-black/30 dark:border-white/40 rounded-full py-5 px-8 lg:py-4 lg:px-6 outline-none focus:border-[#CE010B] dark:focus:border-white/60 disabled:opacity-50 text-xl lg:text-base transition-all"
            >
              <option value="" className="dark:text-black">{loadingBranches ? 'Cargando...' : 'Origen del envío'}</option>
              {(Array.isArray(sucursales) ? sucursales : []).map(suc => <option key={suc.id} value={suc.id} className="dark:text-black">{suc.name}</option>)}
            </select>

            {/* Botón Swap */}
            <button
              type="button"
              onClick={handleSwap}
              disabled={!origen || !destino || loadingRoutes || pasoCheckout > 0}
              className="p-4 bg-white/60 dark:bg-white/10 rounded-full hover:bg-black/5 dark:hover:bg-white/20 text-black dark:text-white transition-all disabled:opacity-30 rotate-90 lg:rotate-0 shadow-sm border border-black/5 dark:border-white/10 lg:bg-transparent lg:dark:bg-transparent lg:border-none lg:shadow-none flex items-center justify-center"
            >
              <ArrowRightLeft size={36} className="lg:w-[28px] lg:h-[28px]" />
            </button>

            {/* Select Destino */}
            <select
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
              disabled={!origen || loadingRoutes || pasoCheckout > 0}
              className="w-full bg-white/50 dark:bg-white/10 dark:text-white border-[1.5px] border-black/30 dark:border-white/40 rounded-full py-5 px-8 lg:py-4 lg:px-6 outline-none focus:border-[#CE010B] dark:focus:border-white/60 disabled:opacity-50 text-xl lg:text-base transition-all"
            >
              <option value="" className="dark:text-black">
                {loadingRoutes ? 'Cargando destinos...' : (!origen ? 'Selecciona origen primero' : 'Destino del envío')}
              </option>
              {(Array.isArray(destinosDisponibles) ? destinosDisponibles : []).map(suc => <option key={suc.id} value={suc.id} className="dark:text-black">{suc.name}</option>)}
            </select>

          </div>
        </div>
      )}

      {/* TARJETA 2: PAQUETES (Paso 0) */}
      {origen && destino && pasoCheckout === 0 && (
        <form
          onSubmit={handleCotizar}
          className="w-full bg-[#EBEBEB] dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] border-[1px] border-black/20 dark:border-white/30 rounded-3xl p-8 shadow-md flex flex-col gap-8 animate-fade-in-up transition-all"
        >
          {/* Encabezado del Formulario */}
          <div className="flex justify-between items-center border-b border-black/10 dark:border-white/20 pb-4">
            <h3 className="text-xl font-bold text-black dark:text-white flex items-center gap-3">
              <Package className="text-[#CE010B] dark:text-white" /> Información del Envío
            </h3>
            <button
              type="button"
              onClick={agregarPaquete}
              className="text-base lg:text-sm font-black text-[#CE010B] dark:text-white hover:opacity-80 flex items-center gap-2 transition-all bg-[#CE010B]/10 dark:bg-white/10 lg:bg-transparent dark:lg:bg-transparent px-4 py-2 lg:p-0 rounded-full"
            >
              <Plus size={22} className="lg:w-[18px] lg:h-[18px]" /> Añadir paquete
            </button>
          </div>

          {/* Listado de Paquetes */}
          <div className="flex flex-col gap-8">
            {paquetes.map((paquete, index) => (
              <div
                key={paquete.id}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8 relative bg-white/50 dark:bg-black/20 p-6 rounded-2xl border-[1.5px] border-black/5 dark:border-white/10 shadow-inner"
              >
                {/* Badge del Paquete */}
                <div className="absolute -top-3 left-6 bg-[#CE010B] dark:bg-white dark:text-black text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                  Paquete {index + 1}
                </div>

                {/* Botón Eliminar */}
                {paquetes.length > 1 && (
                  <button
                    type="button"
                    onClick={() => eliminarPaquete(paquete.id)}
                    className="absolute top-3 right-3 p-2 bg-red-100 hover:bg-red-200 dark:bg-red-950/40 dark:hover:bg-red-900/60 lg:bg-transparent lg:dark:bg-transparent rounded-full text-red-600 dark:text-red-400 lg:text-gray-400 lg:dark:text-white/50 hover:text-red-700 dark:hover:text-white transition-all shadow-sm lg:shadow-none"
                  >
                    <Trash2 size={26} className="lg:w-[20px] lg:h-[20px]" />
                  </button>
                )}

                {/* Columna Izquierda: Peso y Medidas */}
                <div className="flex flex-col gap-6 mt-2">
                  <div className="flex flex-col gap-2">
                    <label className="font-bold flex items-center gap-2 text-base lg:text-sm text-black dark:text-white">
                      <Scale size={18} /> Peso Físico (kg) *
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      required
                      placeholder="0.0"
                      value={paquete.peso}
                      onChange={(e) => {
                        let val = e.target.value.replace(',', '.');
                        val = val.replace(/[^0-9.]/g, '');
                        const partes = val.split('.');
                        if (partes.length <= 2) {
                          if (val === '' || (Number(val) >= 0 && Number(val) <= 65)) {
                            actualizarPaquete(paquete.id, 'peso', val);
                          }
                        }
                      }}
                      className="bg-white dark:bg-white/10 border-[1.5px] border-black/20 dark:border-white/20 dark:text-white rounded-xl px-4 py-4 outline-none focus:border-[#CE010B] dark:focus:border-white transition-all placeholder:dark:text-white/30 text-lg lg:text-base"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <label className="font-bold flex items-center gap-2 text-base lg:text-sm text-black dark:text-white">
                      <Ruler size={18} /> Dimensiones (cm)
                    </label>
                    <div className="flex gap-3">
                      {['largo', 'ancho', 'alto'].map((dim, i) => (
                        <input
                          key={dim}
                          type="text"
                          inputMode="numeric"
                          placeholder={dim.charAt(0).toUpperCase()}
                          value={paquete.dimensiones[dim]}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            if (val === '' || (Number(val) > 0 && Number(val) <= 250)) {
                              actualizarPaquete(paquete.id, dim, val);
                            }
                          }}
                          className="w-1/3 bg-white dark:bg-white/10 border-[1.5px] border-black/20 dark:border-white/20 dark:text-white rounded-xl px-2 py-4 text-center outline-none focus:border-[#CE010B] dark:focus:border-white transition-all text-lg lg:text-base"
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Columna Derecha: Descripción */}
                <div className="flex flex-col gap-2 mt-2 h-full">
                  <label className="font-bold flex items-center gap-2 text-base lg:text-sm text-black dark:text-white">
                    <FileText size={18} /> Descripción del paquete
                    <span className="text-black/40 dark:text-white/40 text-xs font-normal ml-auto">Opcional</span>
                  </label>
                  <textarea
                    value={paquete.descripcion}
                    onChange={(e) => actualizarPaquete(paquete.id, 'descripcion', e.target.value)}
                    placeholder="Ej. Refacciones, ropa, documentos..."
                    className="w-full h-full min-h-[140px] resize-none bg-white dark:bg-white/10 border-[1.5px] border-black/20 dark:border-white/20 dark:text-white rounded-xl px-4 py-4 outline-none focus:border-[#CE010B] dark:focus:border-white transition-all placeholder:dark:text-white/30 text-lg lg:text-base"
                  />
                </div>
              </div>
            ))}
          </div>
          <Turnstile
            ref={turnstileRef}
            siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || "0x4AAAAAAEjLUdPNzXBAhwEd"}
            onSuccess={(token) => setCaptchaToken(token)}
            options={{
              theme: 'light',
            }}
          />
          <button
            type="submit"
            disabled={loadingQuote}
            className="mt-4 bg-[#CE010B] dark:bg-white hover:bg-red-800 dark:hover:bg-gray-200 text-white dark:text-black font-bold text-lg py-4 rounded-xl flex justify-center items-center gap-3 w-full shadow-lg transition-all active:scale-95 disabled:opacity-50"
          >
            {loadingQuote ? (
              <Loader2 className="animate-spin w-6 h-6" />
            ) : (
              <>Calcular Tarifa Exacta <Calculator size={20} /></>
            )}
          </button>
        </form>
      )}

      {/* TARJETA 3: RESULTADOS (Paso 0) */}
      {resultado && pasoCheckout === 0 && (
        <div className="w-full mt-4 animate-fade-in-up flex flex-col gap-0">

          {/* Banner Informativo de Horario */}
          <div className="bg-[#CE010B] dark:bg-[#400000] border border-[#CE010B] dark:border-white/20 rounded-t-3xl p-4 text-center">
            <p className="text-white font-bold text-sm md:text-base drop-shadow-sm">
              {esAntesDeCorte ? "⏱️ Entregue su paquete en sucursal antes de las 13:00 hrs para que su envío Express salga hoy mismo." : "🌙 El horario de corte (13:00 hrs) ha pasado. Su envío saldrá a primera hora del día de mañana."}
            </p>
          </div>

          {/* Cuerpo de Resultados con el Degradado Corporativo */}
          <div className="w-full bg-[#EBEBEB] dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] border-[1px] border-t-0 border-black/20 dark:border-white/20 rounded-b-3xl p-6 md:p-10 shadow-xl flex flex-col gap-8 transition-all">

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full items-stretch">

              {/* OPCIÓN NORMAL */}
              <div
                onClick={() => setTipoEnvio('normal')}
                className={`relative flex flex-col items-center p-6 rounded-3xl border-[2px] transition-all cursor-pointer h-full 
                  ${tipoEnvio === 'normal'
                    ? 'border-[#CE010B] bg-white dark:bg-white/20 dark:border-white shadow-lg scale-[1.02]'
                    : 'border-black/10 bg-white/50 dark:bg-white/5 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/10'}`}
              >
                {tipoEnvio === 'normal' && <CheckCircle2 className="absolute top-4 right-4 text-[#CE010B] dark:text-white" size={24} />}
                <h4 className="text-2xl font-bold text-black dark:text-white mb-4">Envío Normal</h4>
                <div className="h-24 w-40 flex items-center justify-center mb-4">
                  {/* IMAGEN MODO CLARO: Se muestra por defecto, se oculta (hidden) en dark mode */}
                  <img
                    src="/envios1.png"
                    alt="Envío Normal"
                    className="max-h-full max-w-full object-contain block dark:hidden"
                  />

                  {/* IMAGEN MODO OSCURO: Está oculta por defecto, se muestra (block) en dark mode */}
                  <img
                    src="/camioneta3.png"
                    alt="Envío Normal"
                    className="max-h-full max-w-full object-contain hidden dark:block"
                  />
                </div>
                <div className="w-full border-b border-black/10 dark:border-white/10 my-4"></div>
                <div className="text-center flex-grow flex flex-col justify-between w-full">
                  <div className="mb-6">
                    <p className="text-sm font-bold text-gray-500 dark:text-gray-300 mb-1">Fecha estimada de entrega:</p>
                    <p className="text-lg font-black text-black dark:text-white capitalize">{fechaNormal.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                  </div>
                  <div className="bg-gray-100 dark:bg-black/20 rounded-2xl py-3 px-4 w-full">
                    <p className="text-sm text-gray-500 dark:text-gray-300 font-bold mb-1">Precio Total:</p>
                    <p className="text-2xl font-black text-black dark:text-white">${precioNormal.toFixed(2)} <span className="text-sm font-normal">MXN</span></p>
                  </div>
                </div>
              </div>

              {/* OPCIÓN EXPRESS */}
              <div
                onClick={() => setTipoEnvio('express')}
                className={`relative flex flex-col items-center p-6 rounded-3xl border-[2px] transition-all cursor-pointer h-full 
                  ${tipoEnvio === 'express'
                    ? 'border-[#CE010B] bg-white dark:bg-white/20 dark:border-white shadow-lg scale-[1.02]'
                    : 'border-black/10 bg-white/50 dark:bg-white/5 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/10'}`}
              >
                {tipoEnvio === 'express' && <CheckCircle2 className="absolute top-4 right-4 text-[#CE010B] dark:text-white" size={24} />}
                <h4 className="text-2xl font-bold text-[#CE010B] dark:text-white mb-4">Envío Express</h4>
                <div className="h-24 w-40 flex items-center justify-center mb-4">
                  {/* IMAGEN MODO CLARO: Se muestra por defecto, se oculta (hidden) en dark mode */}
                  <img
                    src="/envios2.png"
                    alt="Envío Express"
                    className="max-h-full max-w-full object-contain block dark:hidden"
                  />

                  {/* IMAGEN MODO OSCURO: Está oculta por defecto, se muestra (block) en dark mode */}
                  <img
                    src="/camioneta4.png"
                    alt="Envío Express"
                    className="max-h-full max-w-full object-contain hidden dark:block"
                  />
                </div>
                <div className="w-full border-b border-black/10 dark:border-white/10 my-4"></div>
                <div className="text-center flex-grow flex flex-col justify-between w-full">
                  <div className="mb-6">
                    <p className="text-sm font-bold text-gray-500 dark:text-gray-300 mb-1">Fecha estimada de entrega:</p>
                    <p className="text-lg font-black text-black dark:text-white capitalize">{fechaExpress.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                  </div>
                  <div className="bg-[#CE010B]/5 dark:bg-white/10 rounded-2xl py-3 px-4 w-full border border-[#CE010B]/20 dark:border-white/20">
                    <p className="text-sm text-[#CE010B] dark:text-white font-bold mb-1">Precio Total:</p>
                    <p className="text-2xl font-black text-[#CE010B] dark:text-white">${precioExpress.toFixed(2)} <span className="text-sm font-normal">MXN</span></p>
                  </div>
                </div>
              </div>
            </div>

            {/* Botón de Acción Final */}
            <div className="w-full flex justify-center mt-8">
              <button
                onClick={iniciarCheckout}
                className="w-full md:w-auto bg-black dark:bg-white text-white dark:text-black font-bold text-lg py-5 px-16 rounded-2xl hover:bg-gray-800 dark:hover:bg-gray-200 transition-all shadow-xl hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-3"
              >
                Continuar a Generar Guía <Check size={24} />
              </button>
            </div>
          </div>
        </div>
      )}


      {/* NUEVA TARJETA 4: FORMULARIO DE CHECKOUT (Paso 1) */}
      {pasoCheckout === 1 && (
        <form
          onSubmit={procesarDatosGuia}
          className="w-full bg-[#EBEBEB] dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] border-[1px] border-black/20 dark:border-white/30 rounded-3xl p-8 shadow-xl animate-fade-in-up flex flex-col gap-10 mt-4 transition-all"
        >

          {/* Encabezado con Resumen de Precio */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center border-b border-black/10 dark:border-white/20 pb-4 gap-4">
            <h3 className="text-2xl font-bold text-black dark:text-white flex items-center gap-3" style={{ fontFamily: "'Secular One', sans-serif" }}>
              <ShieldCheck className="text-[#CE010B] dark:text-white" size={28} /> Datos de Envío
            </h3>

            {/* Burbuja de Resumen */}
            <div className="bg-white dark:bg-white/10 px-4 py-2 rounded-full border border-black/10 dark:border-white/20 flex items-center gap-3 shadow-sm text-sm">
              <span className="font-bold capitalize dark:text-white">{tipoEnvio}</span>
              <span className="text-gray-400 dark:text-white/40">|</span>
              <span className="font-black text-[#CE010B] dark:text-white">${tipoEnvio === 'express' ? precioExpress.toFixed(2) : precioNormal.toFixed(2)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">

            {/* COLUMNA 1: REMITENTE */}
            <div className="flex flex-col gap-6">
              <h4 className="font-bold text-lg text-[#CE010B] dark:text-white flex items-center gap-2 border-b border-[#CE010B]/20 dark:border-white/20 pb-2">
                <MapPin size={20} /> Origen: Remitente
              </h4>

              <p className="text-xs text-gray-500 dark:text-gray-300 italic">
                {authenticated
                  ? "Información vinculada a tu cuenta oficial."
                  : "Quien deposita el paquete en sucursal origen."}
              </p>

              {/* Campo: Nombre */}
              <div className="flex flex-col gap-2">
                <label className="font-bold text-base lg:text-sm flex items-center gap-2 dark:text-white"><User size={18} /> Nombre Completo *</label>
                <input
                  type="text"
                  required
                  value={datosRemitente.nombre}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z\sÁÉÍÓÚáéíóúÑñ]/g, '');
                    setDatosRemitente({ ...datosRemitente, nombre: value });
                  }}
                  placeholder="Ej. Juan Pérez García"
                  readOnly={authenticated}
                  className={`w-full border-[1.5px] rounded-xl px-4 py-4 outline-none transition-all text-lg lg:text-base ${authenticated
                      ? "bg-gray-100 dark:bg-white/5 border-black/10 text-gray-500 cursor-not-allowed opacity-70 shadow-none"
                      : "bg-white dark:bg-white/10 border-black/20 dark:border-white/20 dark:text-white focus:border-[#CE010B]"
                    }`}
                />
              </div>

              {/* Campo: Teléfono */}
              <div className="flex flex-col gap-2">
                <label className="font-bold text-base lg:text-sm flex items-center gap-2 dark:text-white">
                  <Phone size={18} /> Teléfono Móvil (WhatsApp) *
                </label>
                <PhoneInput
                  international
                  defaultCountry="MX"
                  value={datosRemitente.telefono}
                  onChange={(value) => {
                    const soloNumeros = (value || '').replace(/\D/g, '');
                    if (soloNumeros.length <= 13) {
                      setDatosRemitente({ ...datosRemitente, telefono: value });
                    }
                  }}
                  numberInputProps={{ maxLength: 16 }}
                  readOnly={authenticated}
                  className={`w-full border-[1.5px] rounded-xl px-4 py-4 outline-none transition-all flex items-center text-lg lg:text-base ${authenticated
                      ? "bg-gray-100 dark:bg-white/5 border-black/10 text-gray-500 cursor-not-allowed opacity-70"
                      : "bg-white dark:bg-white/10 border-black/20 dark:border-white/20 dark:text-white focus-within:border-[#CE010B]"
                    }`}
                />
                <p className="text-[10px] text-gray-400">
                  * Asegúrate de incluir el código de país para recibir notificaciones de WhatsApp.
                </p>
              </div>

              {/* Campo: Correo */}
              <div className="flex flex-col gap-2">
                <label className="font-bold text-base lg:text-sm flex items-center gap-2 dark:text-white"><Mail size={18} /> Correo Electrónico *</label>
                <input
                  type="email"
                  required
                  value={datosRemitente.correo}
                  onChange={e => setDatosRemitente({ ...datosRemitente, correo: e.target.value })}
                  placeholder="Para enviar la guía y recibo"
                  readOnly={authenticated}
                  className={`w-full border-[1.5px] rounded-xl px-4 py-4 outline-none transition-all text-lg lg:text-base ${authenticated
                      ? "bg-gray-100 dark:bg-white/5 border-black/10 text-gray-500 cursor-not-allowed opacity-70 shadow-none"
                      : "bg-white dark:bg-white/10 border-black/20 dark:border-white/20 dark:text-white focus:border-[#CE010B]"
                    }`}
                />
              </div>
            </div>

            {/* COLUMNA 2: DESTINATARIO */}
            <div className="flex flex-col gap-6">
              <h4 className="font-bold text-lg text-black dark:text-white flex items-center gap-2 border-b border-black/20 dark:border-white/20 pb-2">
                <MapPin size={20} /> Destino: Receptor
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-300">
                Quien recoge el paquete en sucursal destino. <strong className="text-black dark:text-white underline underline-offset-2">Debe presentar INE.</strong>
              </p>

              {/* Campo: Nombre */}
              <div className="flex flex-col gap-2">
                <label className="font-bold text-base lg:text-sm flex items-center gap-2 dark:text-white">
                  <User size={18} /> Nombre Completo (Como en INE) *
                </label>
                <input
                  type="text"
                  required
                  value={datosDestinatario.nombre}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z\sÁÉÍÓÚáéíóúÑñ]/g, '');
                    setDatosDestinatario({ ...datosDestinatario, nombre: value });
                  }}
                  placeholder="Ej. María López Hernández"
                  className="w-full bg-white dark:bg-white/10 border-[1.5px] border-black/20 dark:border-white/20 dark:text-white rounded-xl px-4 py-4 outline-none focus:border-black dark:focus:border-white placeholder:dark:text-white/30 text-lg lg:text-base transition-all"
                />
              </div>

              {/* Campo: Teléfono */}
              <div className="flex flex-col gap-2">
                <label className="font-bold text-base lg:text-sm flex items-center gap-2 dark:text-white">
                  <Phone size={18} /> Teléfono Móvil *
                </label>
                <PhoneInput
                  international
                  defaultCountry="MX"
                  value={datosDestinatario.telefono}
                  onChange={(value) => {
                    const soloNumeros = (value || '').replace(/\D/g, '');
                    if (soloNumeros.length <= 13) {
                      setDatosDestinatario({ ...datosDestinatario, telefono: value });
                    }
                  }}
                  numberInputProps={{ maxLength: 16 }}
                  className="w-full bg-white dark:bg-white/10 border-[1.5px] border-black/20 dark:border-white/20 dark:text-white rounded-xl px-4 py-4 outline-none focus-within:border-black dark:focus-within:border-white transition-all text-lg lg:text-base [&_input]:bg-transparent [&_input]:outline-none"
                />
                <p className="text-[10px] text-gray-400">
                  * Incluye código de país para notificaciones automáticas de llegada.
                </p>
              </div>
            </div>
          </div>

          {/* CHECKBOX DE TÉRMINOS Y ENLACES LEGALES */}
          <div className="w-full bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-2xl p-5 mt-2 transition-colors">
            {/* El Checkbox de aceptación */}
            <div className="flex items-start gap-4">
              <input
                type="checkbox"
                id="legal-check"
                required
                checked={confirmaContenido}
                onChange={(e) => setConfirmaContenido(e.target.checked)}
                className="mt-1 w-6 h-6 lg:w-5 lg:h-5 accent-[#CE010B] dark:accent-white cursor-pointer flex-shrink-0"
              />
              <label htmlFor="legal-check" className="text-base lg:text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                He leído y acepto los <strong className="text-black dark:text-white">Términos y Condiciones</strong> además del <strong className="text-black dark:text-white">Aviso de Privacidad</strong>.
              </label>
            </div>

            {/* Botones de navegación para los documentos */}
            <div className="flex flex-wrap gap-3 mt-4 ml-10">
              <a
                href="/terminos"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs lg:text-[11px] font-black uppercase tracking-widest text-[#CE010B] dark:text-red-400 hover:underline transition-all"
              >
                Ver Términos y Condiciones
              </a>

              <span className="text-gray-300 dark:text-gray-600">|</span>

              <a
                href="/privacidad"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs lg:text-[11px] font-black uppercase tracking-widest text-[#CE010B] dark:text-red-400 hover:underline transition-all"
              >
                Ver Aviso de Privacidad
              </a>
            </div>
          </div>

          {/* BOTONES DE ACCIÓN FINALES */}
          <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-4 mt-2 border-t border-black/10 dark:border-white/10 pt-8">
            <button
              type="button"
              onClick={() => setPasoCheckout(0)}
              className="w-full lg:w-auto font-bold text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white py-3 px-6 transition-colors order-2 lg:order-1"
            >
              ← Regresar a tarifas
            </button>
            <button
              type="submit"
              className="w-full lg:w-auto bg-black dark:bg-white text-white dark:text-black font-bold text-lg py-5 px-16 rounded-2xl hover:bg-gray-800 dark:hover:bg-gray-200 transition-all shadow-xl active:scale-95 flex items-center justify-center gap-3 order-1 lg:order-2"
            >
              Proceder al Pago <CreditCard size={24} />
            </button>
          </div>

        </form>
      )}

      {/* ESTADO FINAL (Paso 2): Confirmación y Pago */}
      {pasoCheckout === 2 && resultadoFinal && (
        <div className="w-full animate-fade-in-up mt-4 grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* COLUMNA IZQUIERDA: Resumen de Guía */}
          <div className="bg-white dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] border-[1px] dark:border-white rounded-3xl p-8 shadow-xl flex flex-col items-center text-center gap-4">
            <div className="p-3 rounded-full bg-green-100 text-green-600 dark:bg-white/10 dark:text-white border-2 border-transparent dark:border-white">
              <CheckCircle2 size={40} />
            </div>

            <h3 className="text-2xl font-bold dark:text-white" style={{ fontFamily: "'Secular One', sans-serif" }}>
              ¡Guía Generada!
            </h3>

            <div className="p-4 rounded-xl w-full bg-gray-50 dark:bg-black/20 border-2 border-dashed border-gray-200 dark:border-white">
              <p className="text-sm lg:text-[10px] text-gray-400 dark:text-white/70 uppercase font-black tracking-widest mb-1">Rastreo</p>
              <p className="text-4xl lg:text-3xl font-black text-[#CE010B] dark:text-white">{resultadoFinal.tracking_number}</p>
            </div>

            {/* 🛡️ BOTÓN INTELIGENTE CON ALIAS LOCKICON */}
            {isGuideReady ? (
              <a
                // Nota: Asegúrate que el prefijo /api esté presente si tu ruta es de API
                href={`${import.meta.env.VITE_BACKEND_URL}/api/shipments/${resultadoFinal.tracking_number}/label`}
                target="_blank"
                rel="noreferrer"
                className="w-full flex items-center justify-center gap-3 font-bold py-5 px-8 lg:py-3 lg:px-6 rounded-2xl lg:rounded-xl bg-gray-900 text-white dark:bg-white dark:text-black hover:scale-105 transition-transform uppercase text-xl lg:text-sm"
              >
                <FileText size={24} className="lg:w-[18px] lg:h-[18px]" /> Descargar PDF
              </a>
            ) : (
              <div className="w-full flex items-center justify-center gap-2 font-bold py-3 px-6 rounded-xl bg-gray-100 text-gray-400 dark:bg-white/10 dark:text-white/30 cursor-wait border-2 border-dashed border-gray-300 dark:border-white/10 uppercase text-sm">
                {isCheckingEstatus ? (
                  <>Confirmando Pago... <Loader2 size={18} className="animate-spin ml-2" /></>
                ) : (
                  <>Pago Requerido <LockIcon size={18} className="ml-2" /></> // 👈 Aquí usamos el alias
                )}
              </div>
            )}

            <div className="w-full h-[1px] bg-gray-100 dark:bg-white/10 my-2"></div>

            <div className="w-full text-left space-y-2">
              <div className="flex justify-between text-sm"><span className="text-gray-500 dark:text-white/60">Subtotal:</span> <span className="font-bold dark:text-white">${resultadoFinal.total_price}</span></div>
              <div className="flex justify-between text-lg font-black border-t pt-2 dark:border-white/20"><span className="dark:text-white">TOTAL:</span> <span className="text-[#CE010B] dark:text-white">${resultadoFinal.total_price} MXN</span></div>
            </div>
          </div>

          {/* COLUMNA DERECHA: Interfaz de Pago o Confirmación de Pago */}
          <div className="bg-white dark:bg-[#590000]/60 rounded-3xl p-1 shadow-xl border border-black/5 dark:border-white/10 border-t-4 border-t-[#CE010B] dark:border-t-white overflow-hidden">
            {!paymentResult ? (
              <CheckoutPayment
                shipmentData={resultadoFinal}
                amountToPay={resultadoFinal.total_price}
                onPaymentSuccess={(result) => setPaymentResult(result)}
              />
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-10 text-center animate-bounce-in">
                {paymentResult.status === 'approved' ? (
                  <>
                    <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-green-500/50">
                      <Check size={45} color="white" strokeWidth={4} />
                    </div>
                    <h4 className="text-2xl font-black text-gray-800 dark:text-white uppercase">¡Pago Exitoso!</h4>
                    <p className="text-gray-500 dark:text-white/70 mt-2">Hemos liberado tu envío para recolección inmediata.</p>
                  </>
                ) : (
                  <>
                    <FileText className="text-blue-500 mb-4" size={64} />
                    <h4 className="text-2xl font-black text-gray-800 dark:text-white">Pago Pendiente</h4>
                    {/* 💡 Texto Dinámico para la tienda */}
                    <p className="text-gray-500 dark:text-white/70 mt-2">Completa tu pago en <span className="font-bold">{labelTienda}</span> usando el ticket.</p>

                    <a
                      href={paymentResult.external_resource_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-6 bg-[#CE010B] text-white px-8 py-3 rounded-full font-bold shadow-lg flex items-center justify-center gap-2 uppercase"
                    >
                      {/* 💡 Botón Dinámico */}
                      Ver Ticket {labelTienda} <CreditCard size={20} />
                    </a>
                  </>
                )}
                <button
                  onClick={() => window.location.reload()}
                  className="mt-10 text-sm font-bold text-gray-400 hover:text-[#CE010B] uppercase tracking-widest"
                >
                  Hacer otro envío
                </button>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

{/* ========================================================
    0. COMPONENTE: PANTALLA DIVIDIDA DE SELECCIÓN DE SERVICIO
    ======================================================== */}
function SeleccionServicioSplit({ onSeleccionar }) {
  const [sucursales, setSucursales] = useState([]);
  const [loadingSucursales, setLoadingSucursales] = useState(false);
  const [mostrarModalWhatsApp, setMostrarModalWhatsApp] = useState(false);
  const [hoveredSide, setHoveredSide] = useState(null);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        setLoadingSucursales(true);
        const res = await api.get('/branches');
        const data = res.data?.data || res.data;
        if (Array.isArray(data)) {
          setSucursales(data);
        }
      } catch (err) {
        console.error("Error al cargar sucursales para WhatsApp:", err);
      } finally {
        setLoadingSucursales(false);
      }
    };
    fetchBranches();
  }, []);

  const sucursalesConTelefono = (Array.isArray(sucursales) ? sucursales : []).filter((s) => {
    const tel = (s.phone || s.telefono || '').toString().trim();
    return tel.length >= 7;
  });

  const abrirWhatsAppSucursal = (sucursal) => {
    const rawPhone = (sucursal?.phone || sucursal?.telefono || '').toString();
    const cleanPhone = rawPhone.replace(/\D/g, '');
    if (!cleanPhone) return;
    const phoneWithCountry = cleanPhone.length === 10 ? `52${cleanPhone}` : cleanPhone;
    const msg = `¡Hola Más Envíos! Me gustaría cotizar un envío de Paquetería Nacional desde la sucursal de ${sucursal?.name || sucursal?.city || 'su punto de atención'}.`;
    window.open(`https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(msg)}`, '_blank');
    setMostrarModalWhatsApp(false);
  };

  const paqueteriasAliadas = [
    { nombre: 'DHL', color: 'bg-[#FFCC00]/20 text-[#D40511] border-[#FFCC00]/40 dark:bg-[#FFCC00]/20 dark:text-[#FFCC00] dark:border-[#FFCC00]/50' },
    { nombre: 'FedEx', color: 'bg-[#4D148C]/15 text-[#4D148C] border-[#4D148C]/30 dark:bg-[#FF6600]/20 dark:text-[#FF6600] dark:border-[#FF6600]/50' },
    { nombre: 'Estafeta', color: 'bg-[#CE010B]/15 text-[#CE010B] border-[#CE010B]/30 dark:bg-[#CE010B]/30 dark:text-red-300 dark:border-red-400/40' },
    { nombre: 'Paquetexpress', color: 'bg-blue-500/15 text-blue-700 border-blue-500/30 dark:bg-blue-500/25 dark:text-blue-300 dark:border-blue-400/40' },
    { nombre: 'Tres Guerras', color: 'bg-amber-500/15 text-amber-800 border-amber-500/30 dark:bg-amber-500/25 dark:text-amber-300 dark:border-amber-400/40' },
    { nombre: 'UPS', color: 'bg-black/10 text-black border-black/30 dark:bg-black/70 dark:text-white dark:border-white/30 font-black' },
  ];

  return (
    <div className="w-full flex flex-col items-center">
      
      {/* Encabezado Superior con Alto Contraste Integrado al Fondo Global */}
      <div className="w-full relative overflow-hidden mb-6 md:mb-10">
        <div className="absolute inset-0 bg-gradient-to-b from-[#CE010B]/80 via-black/70 to-transparent dark:from-[#590000]/90 dark:via-black/80 dark:to-transparent pointer-events-none" />

        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 pt-10 pb-12 flex flex-col items-center text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 border border-white/30 text-white text-xs md:text-sm font-black uppercase tracking-wider mb-4 shadow-sm backdrop-blur-md animate-pulse">
            <Sparkles size={16} className="text-amber-300" /> Centro de Cotizaciones Más Envíos
          </div>
          <h1 
            className="text-[34px] md:text-[46px] lg:text-[52px] font-black text-white uppercase tracking-tight leading-tight drop-shadow-lg"
            style={{ fontFamily: "'Secular One', sans-serif" }}
          >
            Elige tu tipo de servicio
          </h1>
          <p className="text-white/95 text-[15px] md:text-[18px] font-medium max-w-[760px] leading-relaxed mt-3 drop-shadow-md">
            Selecciona la modalidad de envío que mejor se adapte a tus necesidades de tiempo, destino y presupuesto.
          </p>
          <div className="w-full max-w-[700px] h-[2px] bg-gradient-to-r from-transparent via-white/40 to-transparent mt-6" />
        </div>
      </div>

      {/* Grid Pantalla Dividida (50/50 Desktop, Apilado en Móvil) */}
      <div className="w-full max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* ========================================================
            MITAD IZQUIERDA: SERVICIO OCURRE REGIONAL
            ======================================================== */}
        <div 
          onMouseEnter={() => setHoveredSide('ocurre')}
          onMouseLeave={() => setHoveredSide(null)}
          className={`relative overflow-hidden rounded-3xl p-8 lg:p-12 transition-all duration-500 flex flex-col justify-between border-[2px] cursor-pointer group
            ${hoveredSide === 'nacional' ? 'opacity-85 scale-[0.99]' : 'scale-100'}
            border-black/15 bg-gradient-to-b from-[#F9F9F9] to-[#E3E3E3] hover:border-[#CE010B] hover:shadow-[0_20px_50px_rgba(206,1,11,0.15)] hover:scale-[1.015]
            dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-white/20 hover:dark:border-white hover:dark:shadow-[0_0_40px_rgba(255,255,255,0.2)]`}
          onClick={() => onSeleccionar('ocurre')}
        >
          {/* Fondo Decorativo Sutil */}
          <div className="absolute -right-12 -bottom-12 opacity-5 dark:opacity-10 pointer-events-none group-hover:scale-110 transition-transform duration-700">
            <Truck size={280} className="text-black dark:text-white" />
          </div>

          <div>
            {/* Badge Superior */}
            <div className="flex items-center justify-between gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-black uppercase tracking-wider bg-[#CE010B] text-white shadow-md">
                <Truck size={14} /> Servicio Express & Ocurre
              </span>
              <span className="text-xs font-bold text-[#CE010B] dark:text-white/80 bg-white/80 dark:bg-black/30 px-3 py-1 rounded-full border border-black/5 dark:border-white/10">
                ⚡ En Línea 24/7
              </span>
            </div>

            {/* Título y Destinos */}
            <h2 
              className="text-3xl lg:text-4xl font-black text-black dark:text-white tracking-tight leading-tight uppercase group-hover:text-[#CE010B] dark:group-hover:text-white transition-colors"
              style={{ fontFamily: "'Secular One', sans-serif" }}
            >
              Servicio Ocurre
            </h2>
            <div className="flex flex-wrap items-center gap-2 my-3">
              <span className="bg-[#CE010B]/10 dark:bg-white/15 text-[#CE010B] dark:text-white font-extrabold px-3 py-1 rounded-lg text-sm md:text-base border border-[#CE010B]/20 dark:border-white/20">
                Pachuca
              </span>
              <span className="text-[#CE010B] dark:text-white font-black">⇄</span>
              <span className="bg-[#CE010B]/10 dark:bg-white/15 text-[#CE010B] dark:text-white font-extrabold px-3 py-1 rounded-lg text-sm md:text-base border border-[#CE010B]/20 dark:border-white/20">
                CDMX
              </span>
              <span className="text-[#CE010B] dark:text-white font-black">⇄</span>
              <span className="bg-[#CE010B]/10 dark:bg-white/15 text-[#CE010B] dark:text-white font-extrabold px-3 py-1 rounded-lg text-sm md:text-base border border-[#CE010B]/20 dark:border-white/20">
                Toluca
              </span>
            </div>

            <p className="text-gray-700 dark:text-gray-200 text-base md:text-lg font-medium leading-relaxed mt-4">
              Envíos directos punto a punto entre nuestras sucursales y centros de distribución. Cotiza al instante, paga de forma segura en línea y obtén tu guía oficial de inmediato.
            </p>

            {/* Lista de Beneficios */}
            <ul className="space-y-3 mt-6">
              <li className="flex items-center gap-3 text-sm md:text-base font-bold text-gray-800 dark:text-white/90">
                <CheckCircle2 size={20} className="text-[#CE010B] dark:text-white shrink-0" />
                Entrega el mismo día o al día siguiente (Estándar / Express)
              </li>
              <li className="flex items-center gap-3 text-sm md:text-base font-bold text-gray-800 dark:text-white/90">
                <CheckCircle2 size={20} className="text-[#CE010B] dark:text-white shrink-0" />
                Pago digital con Tarjeta, Transferencia SPEI u OXXO
              </li>
              <li className="flex items-center gap-3 text-sm md:text-base font-bold text-gray-800 dark:text-white/90">
                <CheckCircle2 size={20} className="text-[#CE010B] dark:text-white shrink-0" />
                Generación y descarga inmediata de Guía oficial en PDF
              </li>
              <li className="flex items-center gap-3 text-sm md:text-base font-bold text-gray-800 dark:text-white/90">
                <CheckCircle2 size={20} className="text-[#CE010B] dark:text-white shrink-0" />
                Rastreo satelital en vivo de tus paquetes
              </li>
            </ul>
          </div>

          {/* Botón CTA */}
          <div className="mt-8 pt-6 border-t border-black/10 dark:border-white/10">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSeleccionar('ocurre');
              }}
              className="w-full bg-[#CE010B] hover:bg-[#b00109] dark:bg-white dark:hover:bg-gray-100 text-white dark:text-[#590000] py-4 md:py-5 px-8 rounded-2xl font-black text-lg md:text-xl uppercase tracking-wider shadow-lg flex items-center justify-center gap-3 group-hover:gap-4 transition-all duration-300"
            >
              <span>Cotizar en Línea</span>
              <ArrowRight size={24} className="group-hover:translate-x-1.5 transition-transform" />
            </button>
          </div>
        </div>


        {/* ========================================================
            MITAD DERECHA: SERVICIO DE PAQUETERÍA NACIONAL
            ======================================================== */}
        <div 
          onMouseEnter={() => setHoveredSide('nacional')}
          onMouseLeave={() => setHoveredSide(null)}
          className={`relative overflow-hidden rounded-3xl p-8 lg:p-12 transition-all duration-500 flex flex-col justify-between border-[2px] group
            ${hoveredSide === 'ocurre' ? 'opacity-85 scale-[0.99]' : 'scale-100'}
            border-black/15 bg-gradient-to-b from-[#F9F9F9] to-[#E3E3E3] hover:border-[#25D366] hover:shadow-[0_20px_50px_rgba(37,211,102,0.15)] hover:scale-[1.015]
            dark:bg-gradient-to-b dark:from-[#590000] dark:to-[#780000] dark:border-white/20 hover:dark:border-white hover:dark:shadow-[0_0_40px_rgba(255,255,255,0.2)]`}
        >
          {/* Fondo Decorativo Sutil */}
          <div className="absolute -right-12 -bottom-12 opacity-5 dark:opacity-10 pointer-events-none group-hover:scale-110 transition-transform duration-700">
            <Globe size={280} className="text-black dark:text-white" />
          </div>

          <div>
            {/* Badge Superior */}
            <div className="flex items-center justify-between gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-black uppercase tracking-wider bg-emerald-600 text-white shadow-md">
                <Globe size={14} /> Cobertura Nacional e Internacional
              </span>
              <span className="text-xs font-bold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 px-3 py-1 rounded-full border border-emerald-500/20">
                🤝 Alianzas Comerciales
              </span>
            </div>

            {/* Título */}
            <h2 
              className="text-3xl lg:text-4xl font-black text-black dark:text-white tracking-tight leading-tight uppercase group-hover:text-emerald-700 dark:group-hover:text-white transition-colors"
              style={{ fontFamily: "'Secular One', sans-serif" }}
            >
              Paquetería Nacional e Internacional
            </h2>
            <p className="text-sm font-bold text-gray-500 dark:text-gray-300 uppercase tracking-wider my-2">
              Envíos a cualquier destino en México y el mundo
            </p>

            <p className="text-gray-700 dark:text-gray-200 text-base md:text-lg font-medium leading-relaxed mt-3">
              Gestionamos tus envíos nacionales e internacionales conectando con las mejores redes logísticas del país y del mundo con tarifas competitivas y asesoría personalizada.
            </p>

            {/* Grid de Paqueterías Aliadas */}
            <div className="mt-6">
              <p className="text-xs font-bold text-gray-500 dark:text-gray-300 uppercase tracking-widest mb-3">
                Alianzas y Convenios Disponibles:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {paqueteriasAliadas.map((paq) => (
                  <div
                    key={paq.nombre}
                    className={`px-3.5 py-2.5 rounded-xl border text-center font-black text-sm tracking-wide shadow-sm backdrop-blur-sm transition-transform hover:scale-105 ${paq.color}`}
                  >
                    {paq.nombre}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sección de Contacto WhatsApp + Leyenda Próximamente */}
          <div className="mt-8 pt-6 border-t border-black/10 dark:border-white/10 flex flex-col gap-4">
            
            {/* Botón WhatsApp */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setMostrarModalWhatsApp(!mostrarModalWhatsApp)}
                className="w-full bg-[#25D366] hover:bg-[#20ba59] text-white py-4 md:py-5 px-8 rounded-2xl font-black text-lg md:text-xl uppercase tracking-wider shadow-lg flex items-center justify-center gap-3 group-hover:gap-4 transition-all duration-300"
              >
                <MessageCircle size={26} className="shrink-0" />
                <span>Cotizar por WhatsApp</span>
                <ChevronRight size={22} className={`transition-transform duration-300 ${mostrarModalWhatsApp ? 'rotate-90' : ''}`} />
              </button>

              {/* Menú Desplegable de Sucursales para WhatsApp */}
              {mostrarModalWhatsApp && (
                <div className="mt-3 p-4 bg-white dark:bg-neutral-900 border-2 border-[#25D366] rounded-2xl shadow-2xl animate-in fade-in slide-in-from-top-2 duration-300 z-30">
                  <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2 text-center">
                    Selecciona tu sucursal más cercana para iniciar chat:
                  </p>
                  
                  {loadingSucursales ? (
                    <div className="py-4 text-center text-sm font-semibold text-gray-500 dark:text-gray-300 flex items-center justify-center gap-2">
                      <Loader2 className="animate-spin size-4" /> Cargando sucursales...
                    </div>
                  ) : sucursalesConTelefono.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {sucursalesConTelefono.map((suc) => (
                        <button
                          key={suc.id}
                          type="button"
                          onClick={() => abrirWhatsAppSucursal(suc)}
                          className="flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-[#25D366]/10 dark:bg-white/5 dark:hover:bg-white/15 border border-black/10 dark:border-white/10 text-left transition-all group/suc"
                        >
                          <div>
                            <p className="font-extrabold text-sm text-black dark:text-white group-hover/suc:text-[#25D366] transition-colors">
                              {suc.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {suc.phone || suc.telefono}
                            </p>
                          </div>
                          <ExternalLink size={16} className="text-[#25D366] shrink-0" />
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-white/5 border border-black/10 dark:border-white/10 text-center">
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        Estamos preparándonos para atenderte por este medio.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Leyenda Informativa */}
            <div className="bg-amber-500/10 dark:bg-black/30 border border-amber-500/30 dark:border-white/20 rounded-2xl p-3.5 flex items-center gap-3">
              <div className="p-2 rounded-xl bg-amber-500/20 text-amber-700 dark:text-amber-300 shrink-0">
                <Sparkles size={18} />
              </div>
              <p className="text-xs md:text-sm font-semibold text-gray-700 dark:text-white/80 leading-snug">
                Estamos trabajando para ofrecer el servicio de Paquetería Nacional e Internacional en línea, <span className="font-bold text-amber-700 dark:text-amber-300">¡muy pronto estará disponible directamente en esta plataforma!</span>
              </p>
            </div>

          </div>
        </div>

      </div>

    </div>
  );
}

export default function CotizadorPage() {
  const [servicioSeleccionado, setServicioSeleccionado] = useState(null);

  const handleSeleccionarServicio = (tipo) => {
    setServicioSeleccionado(tipo);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleVolverASeleccion = () => {
    setServicioSeleccionado(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="w-full flex flex-col min-h-screen pb-20 px-4 md:px-0 relative">
      {servicioSeleccionado === null ? (
        <div className="w-full animate-in fade-in zoom-in-95 duration-500">
          <SeleccionServicioSplit onSeleccionar={handleSeleccionarServicio} />
        </div>
      ) : (
        <div className="w-full animate-in fade-in slide-in-from-right-4 duration-500 relative">
          {/* Botón Lateral Flotante a la Izquierda */}
          <div className="fixed left-3 top-20 md:left-6 md:top-24 z-40">
            <button
              type="button"
              onClick={handleVolverASeleccion}
              className="group flex items-center gap-2 bg-white/90 dark:bg-black/85 hover:bg-[#CE010B] dark:hover:bg-[#CE010B] text-gray-800 dark:text-white hover:text-white dark:hover:text-white py-2.5 px-4 rounded-full border border-black/15 dark:border-white/20 shadow-2xl backdrop-blur-md transition-all duration-300 hover:scale-105 active:scale-95"
              title="Volver a seleccionar tipo de servicio"
            >
              <ArrowLeft size={18} className="transition-transform group-hover:-translate-x-1" />
              <span className="text-xs md:text-sm font-extrabold uppercase tracking-wider">
                Servicios
              </span>
            </button>
          </div>

          <HeaderCotizador />
          <FormularioCotizacion />
        </div>
      )}
    </div>
  );
}